import { createAsyncThunk } from '@reduxjs/toolkit'
import { isAxiosError } from 'axios'
import { v4 as uuidv4 } from 'uuid'

import config from '../../config'
import { apiDELETE, apiGET, apiPOST } from '../../lib/api'
import b64 from '../../lib/b64'
import {
  getDeviceIdFromDb as getDeviceIdFromIndexDB,
  saveDeviceIdToDB as saveDeviceIdToIndexDB,
} from '../../lib/data'
import { Device, PushStatus, SerializedPushSubscription, TestPushMessage } from '../../types/Input'
import { serializePushSubscription } from './utils'

const fetchDevices = (): Promise<Device[]> => {
  return apiGET('devices')
}

export const fetchDevicesAction = createAsyncThunk<Device[], void, { rejectValue: string }>(
  'deviceManagement/fetchDevices',
  async (_, { rejectWithValue }) => {
    try {
      return fetchDevices()
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch devices')
    }
  }
)

const handleSubscribe = async (): Promise<{
  subscription: SerializedPushSubscription | null
  pushStatus: PushStatus
}> => {
  try {
    if (!navigator.serviceWorker) {
      return { subscription: null, pushStatus: 'not_possible' }
    }

    const registration = await navigator.serviceWorker.ready
    if (!registration.pushManager) {
      return { subscription: null, pushStatus: 'not_possible' }
    }

    let permission = Notification.permission

    if (permission === 'default') {
      permission = await Notification.requestPermission()
    }

    if (permission !== 'granted') {
      return { subscription: null, pushStatus: 'not_allowed_in_browser' }
    }

    let subscription = await registration.pushManager.getSubscription()

    if (subscription === null) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: b64(config.vapid),
      })
    }

    const serializedSubscription = await serializePushSubscription(subscription)
    return { subscription: serializedSubscription, pushStatus: 'permission_granted' }
  } catch (err) {
    throw new Error('Failed to subscribe to device.')
  }
}

const upsertDevice = async (deviceId: string): Promise<Device> => {
  const { subscription, pushStatus } = await handleSubscribe()
  return await apiPOST('devices', { deviceId, subscription, pushStatus })
}

/**
 * Get Device from backend
 * Also check if it has a pushStatus since some
 * invalid entries were created in the past
 */
const retrieveDeviceInfo = async (deviceId: string): Promise<Device | undefined> => {
  try {
    const device = await apiGET<Device>(`devices/${deviceId}`)
    if (!device.pushStatus) {
      return
    }
    return device
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 404) {
      return
    } else {
      throw error
    }
  }
}

export const initializeDeviceAction = createAsyncThunk<Device, void, { rejectValue: string }>(
  'deviceManagement/initializeDevice',
  async (_, { rejectWithValue }) => {
    try {
      const deviceId = await getDeviceIdFromIndexDB()

      if (deviceId) {
        // Try to get existing valid device, fallback to creating new one
        const existingDevice = await retrieveDeviceInfo(deviceId)
        return existingDevice ?? (await upsertDevice(deviceId))
      }

      // No device in IndexedDB - create new one
      const newDeviceId = uuidv4()
      const createdDevice = await upsertDevice(newDeviceId)
      await saveDeviceIdToIndexDB(newDeviceId)
      return createdDevice
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to initialize device')
    }
  }
)

export const subscribePushNotificationsAction = createAsyncThunk<
  Device,
  string,
  { rejectValue: string }
>('deviceManagement/subscribePushNotifications', async (deviceId, { rejectWithValue }) => {
  try {
    return await upsertDevice(deviceId)
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to subscribe push notifications')
  }
})

const sendUnsubscription = (device: Device): Promise<Device> => {
  return apiPOST('push/unregister', { device })
}

export const unsubscribePushNotificationsAction = createAsyncThunk<
  Device,
  Device,
  { rejectValue: string }
>('deviceManagement/unsubscribePushNotifications', async (device, { rejectWithValue }) => {
  try {
    if (navigator.serviceWorker) {
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription()
      if (!subscription) {
        throw new Error('No subscription found')
      }

      const unsubscribedDevice = { ...device, subscription: null }
      await subscription.unsubscribe()
      return await sendUnsubscription(unsubscribedDevice)
    } else {
      throw new Error('No serviceWorker found')
    }
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to unsubscribe push notifications')
  }
})

const sendTestPushToDevice = (
  payload: DeviceTestMessagePayload
): Promise<DeviceTestMessagePayload> => {
  return apiPOST('devices/test/push', payload)
}

export type DeviceTestMessagePayload = {
  deviceId: string
  testPushMessage: TestPushMessage
}

export const sendTestPushMessageAction = createAsyncThunk<
  DeviceTestMessagePayload,
  DeviceTestMessagePayload,
  { rejectValue: string }
>('deviceManagement/sendTestPushMessage', async (payload, { rejectWithValue }) => {
  try {
    return await sendTestPushToDevice(payload)
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to send test push message')
  }
})

const deleteDevice = (deviceId?: string): Promise<unknown> => apiDELETE(`devices/${deviceId}`)

export const deleteDeviceAction = createAsyncThunk<
  string, // deviceId
  string, // deviceId
  { rejectValue: string }
>('deviceManagement/deleteDevice', async (deviceId, { rejectWithValue }) => {
  try {
    await deleteDevice(deviceId)
    return deviceId
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to delete device')
  }
})

const updateDeviceName = (deviceId: string, newName: string): Promise<Device> => {
  return apiPOST(`/devices/${deviceId}`, { newName })
}

type UpdateDeviceNamePayload = {
  deviceId: string
  newName: string
}

export const updateDeviceNameAction = createAsyncThunk<
  Device,
  UpdateDeviceNamePayload,
  { rejectValue: string }
>('deviceManagement/updateDeviceName', async (payload, { rejectWithValue }) => {
  try {
    return await updateDeviceName(payload.deviceId, payload.newName)
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to update device name')
  }
})
